import { useState, type FormEvent } from 'react'
import { useMutation } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Link, useSearchParams } from 'react-router'
import { passwordIssues } from '@concours/shared'
import { Alert, AuthCard, Button, PasswordInput, useAuthSession } from '@concours/ui'
import { errorMessage, fieldErrors } from '../../lib/errors'

/**
 * Page partagée par la réinitialisation (lien reçu par email). Le token reste
 * dans l'URL seulement le temps de la saisie ; il est à usage unique.
 */
export function ResetPasswordPage({
  loginPath = '/connexion',
  forgotPath = '/mot-de-passe-oublie',
}) {
  const { t } = useTranslation()
  const { api } = useAuthSession()
  const [params] = useSearchParams()
  const token = params.get('token') ?? ''
  const [password, setPassword] = useState('')
  const [confirmation, setConfirmation] = useState('')

  const reset = useMutation({
    mutationFn: () =>
      api.post('/auth/password/reset', { token, password, passwordConfirmation: confirmation }),
  })

  const mismatch = confirmation.length > 0 && confirmation !== password
  const ok = passwordIssues(password).length === 0 && !mismatch && confirmation.length > 0

  function submit(event: FormEvent) {
    event.preventDefault()
    if (ok) reset.mutate()
  }

  if (!token) {
    return (
      <AuthCard title={t('auth.reset.title')}>
        <Alert tone="warning">{t('auth.reset.missingToken')}</Alert>
        <p style={{ marginBlockStart: 'var(--space-4)' }}>
          <Link to={forgotPath}>{t('auth.reset.newRequest')}</Link>
        </p>
      </AuthCard>
    )
  }

  return (
    <AuthCard title={t('auth.reset.title')} description={t('auth.reset.description')}>
      {reset.isSuccess ? (
        <div className="stack">
          <Alert tone="success">{t('auth.reset.done')}</Alert>
          <Link className="btn btn-primary btn-lg btn-block" to={loginPath}>
            {t('auth.login.submit')}
          </Link>
        </div>
      ) : (
        <form className="stack" onSubmit={submit} noValidate>
          {reset.isError && (
            <Alert tone="danger">
              {errorMessage(reset.error)} <Link to={forgotPath}>{t('auth.reset.newRequest')}</Link>
            </Alert>
          )}
          <PasswordInput
            label={t('auth.password')}
            autoComplete="new-password"
            showRules
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            error={fieldErrors(reset.error).password}
          />
          <PasswordInput
            label={t('auth.passwordConfirmation')}
            autoComplete="new-password"
            value={confirmation}
            onChange={(e) => setConfirmation(e.target.value)}
            error={mismatch ? t('auth.mismatch') : undefined}
          />
          <Button
            type="submit"
            variant="primary"
            size="lg"
            block
            loading={reset.isPending}
            disabled={!ok}
          >
            {t('auth.reset.submit')}
          </Button>
        </form>
      )}
    </AuthCard>
  )
}
